import { FilterOptionValue } from 'features/filters/types'

/**
 * De-duplicates filter values by their display name (value property).
 * Multiple options with the same display name but different cluster_uuids
 * are merged into a single entry with all identifiers collected.
 *
 * This matches the V1 behavior in SelectorBody.tsx where duplicate theme
 * names are grouped together.
 *
 * @param values - Array of filter option values to de-duplicate
 * @returns De-duplicated array with identifiers collected per unique display name
 */
export const deduplicateFilterValues = (
  values: FilterOptionValue[],
): FilterOptionValue[] => {
  const grouped: Record<string, { n_posts: number; identifiers: string[] }> = {}

  values.forEach((item) => {
    if (!grouped[item.value]) {
      grouped[item.value] = { n_posts: 0, identifiers: [] }
    }
    grouped[item.value]!.n_posts += item.n_posts ?? 0
    grouped[item.value]!.identifiers.push(item.cluster_uuid ?? item.value)
  })

  return Object.entries(grouped).map(([key, { n_posts, identifiers }]) => ({
    value: key,
    n_posts,
    identifiers,
    cluster_uuid: identifiers[0],
  }))
}

/**
 * Collapses theme display names where parent and child are identical.
 * Examples:
 * - "Bladder & Kidney Health: Bladder & Kidney Health" -> "Bladder & Kidney Health"
 * - "Fans / Attractors" -> "Fans / Attractors" (special case, always collapsed)
 * - "Critics / Detractors" -> "Critics / Detractors" (special case, always collapsed)
 * - "Diet & Weight Management: Diabetes" -> "Diet & Weight Management: Diabetes" (unchanged)
 *
 * This matches the V1 behavior in getBadgeLabels (helpers.ts lines 336-347).
 *
 * @param name - The theme display name to potentially collapse
 * @returns Collapsed display name
 */
export const collapseThemeDisplayName = (name: string): string => {
  const parts = name.split(': ')
  if (
    parts.length > 1 &&
    (parts[0] === parts[1] ||
      name.includes('Fans / Attractors') ||
      name.includes('Critics / Detractors'))
  ) {
    return parts[0]!
  }
  return name
}
